import { useMemo, useCallback } from 'react'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { useNavigation } from '@/hooks/useNavigation'
import { useModalContext } from '../Modal/contexts/ModalContext'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { useQueryClient } from '@tanstack/react-query'
import { useRealtimeAttendanceContext } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import { MenuItem, NAVBAR_MODALS } from './types'
import useStyles from './styles'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { getCache } from '@/utils/cache'
import { IS_PRODUCTION } from '@/constants'
import { useStratificationRules } from '@/modules/YourHealth/hooks/useStratificationRules'
import { useBreakpointMatch } from '@conexasaude/conexa-hero'

import { useSettingUpAnalyticsEvent } from '@/hooks/useSettingUpAnalytics'
import { roomPaths } from '@/modules/Attendance/submodules/Room/constants'
import { useCareContext } from '@/modules/Care/context'
import { useCheckoutStore } from '@/modules/Checkout/stores/checkout.store'
import { useChatStore } from '@/modules/Chat/store'

const TIMEOUT_TO_CLEAR_DATA = 100

export const useNavbar = () => {
  const { clinic, user, signOut, users } = useAuthContext()
  const { pathname } = useLocation()
  const { logoutFromFirebase } = useFirebaseContext()
  const setCard = useCheckoutStore(state => state.setCard)
  const { clearRealtimeAttendance } = useRealtimeAttendanceContext()
  const { hasMentalHealth } = useStratificationRules({
    consultationsQnt: 0,
    hasCurrentCicle: false,
    performedConsultationsQnt: 0,
  })
  const { showModal } = useModalContext()
  const { embed } = useThemeGeneratorContext()
  const resetStore = useChatStore(state => state.resetStore)
  const queryClient = useQueryClient()
  const navigate = useNavigation()
  const waitingRoomMatch = useRouteMatch([
    '/atendimento-agendado/sala-de-espera/:id',
    '/atendimento-imediato/waiting',
    roomPaths.scheduled.fullPath,
    roomPaths.immediate.fullPath,
  ])
  const styles = useStyles({
    embed: embed?.value,
    conexaLogo: !clinic?.logos?.show,
  })

  const { careFeatureFlag } = useCareContext()

  const settingUpAnalyticsEvent = useSettingUpAnalyticsEvent()

  const isUnifiedMultiprofilePreviusRoute = getCache(
    'unified-redirect-multi-profile',
  )

  const logout = useCallback(
    (general: boolean) => {
      signOut(general)
      logoutFromFirebase()
      clearRealtimeAttendance()
      setCard(null)
      queryClient.removeQueries()
      resetStore()

      if (isUnifiedMultiprofilePreviusRoute && IS_PRODUCTION) {
        window.location.href =
          'https://consultorioonline.psicologiaviva.com.br/login'
        return
      }
      if (isUnifiedMultiprofilePreviusRoute && !IS_PRODUCTION) {
        window.location.href =
          'https://hml-consultorioonline.psicologiaviva.com.br/login'
        return
      }

      if (general) {
        setTimeout(() => {
          navigate('/login', { replace: true })
        }, TIMEOUT_TO_CLEAR_DATA)
      }
    },
    [
      navigate,
      signOut,
      logoutFromFirebase,
      clearRealtimeAttendance,
      setCard,
      queryClient,
      isUnifiedMultiprofilePreviusRoute,
      resetStore,
    ],
  )

  const handleChangeProfile = useCallback(() => {
    const isImmediateWaitingRoom = pathname.includes(
      '/atendimento-imediato/waiting',
    )

    const [, firstPathname, secondPathname] = pathname.split('/')
    const isScheduledWaitingRoom =
      firstPathname === 'atendimento-agendado' && /^\d+$/.test(secondPathname)

    const isVideoRoom = pathname.includes('/chamada')

    if (isImmediateWaitingRoom || isScheduledWaitingRoom || isVideoRoom) {
      return
    }

    if (isUnifiedMultiprofilePreviusRoute) {
      navigate('/escolha-unificada')
      return
    }

    logout(false)
    navigate('/escolha', { replace: true })
  }, [navigate, isUnifiedMultiprofilePreviusRoute, logout, pathname])

  const handleLogout = useCallback(() => {
    const isImmediateWaitingRoom = pathname.includes(
      '/atendimento-imediato/waiting',
    )
    const [, firstPathname, secondPathname] = pathname.split('/')
    const isScheduledWaitingRoom =
      firstPathname === 'atendimento-agendado' && /^\d+$/.test(secondPathname)

    if (isImmediateWaitingRoom || isScheduledWaitingRoom) {
      showModal('LEAVE_WAITING_ROOM', {
        title: 'Deseja sair da sala de espera?',
        width: 512,
      })

      return
    }

    logout(true)
  }, [pathname, logout, showModal])

  const showConsultations = useMemo(() => {
    if (
      !clinic?.flag?.HabilitarAgendamentoOperadora &&
      !clinic?.flag?.PossuiAlgumServicoHabilitado &&
      clinic?.flag?.CnxPlus
    ) {
      return false
    }

    if (clinic?.flag?.HabilitarAgendamentoOperadora && clinic?.flag?.CnxPlus) {
      return true
    }

    if (clinic?.flag?.CnxPlus && !clinic?.flag?.PossuiAlgumServicoHabilitado) {
      return false
    }

    return true
  }, [
    clinic?.flag?.CnxPlus,
    clinic?.flag?.HabilitarAgendamentoOperadora,
    clinic?.flag?.PossuiAlgumServicoHabilitado,
  ])

  const isLargeScreen = useBreakpointMatch('lg')

  const handleOpenSOSDialog = useCallback(() => {
    showModal(NAVBAR_MODALS.SOS_MENTAL_HEALTH)

    const isCNXPlusPath = pathname === '/conexa-plus'

    settingUpAnalyticsEvent({
      eventName: isCNXPlusPath ? 'at_cnx_plus' : 'at_home',
      interaction: 'sos_btn_click',
    })
  }, [pathname, settingUpAnalyticsEvent, showModal])

  const menuItems: MenuItem[] = [
    {
      id: 1,
      label: 'Início',
      type: 'link',
      path: '/',
      icon: 'home',
      show: true,
      analyticsEvent: 'Pac_Home_HeaderInicio',
      cy: 'menu-home',
    },
    {
      id: 2,
      label: 'Consultas',
      type: 'link',
      path: '/meus-atendimentos',
      icon: 'folder',
      show: showConsultations,
      analyticsEvent: clinic?.flag?.ClinicaUnificadaPV
        ? 'Pac_Agendamento_MenuConsultas'
        : 'Pac_Home_HeaderConsultas',
      cy: 'menu-consultations',
    },
    {
      id: 3,
      type: 'link',
      path: '/conexa-plus',
      label: 'Conexa Plus',
      icon: 'medical-cross',
      show: clinic?.flag?.CnxPlus,
      analyticsEvent: 'Pac_Home_HeaderCNXPlus',
      cy: 'menu-conexa-plus',
    },

    {
      id: 4,
      type: 'link',
      path: '/sua-saude',
      label: 'Sua saúde',
      icon: 'pulse',
      show:
        clinic?.flag?.HabilitarSuaSaude || hasMentalHealth || careFeatureFlag,
      analyticsEvent: 'Pac_Home_HeaderSuaSaude',
      cy: 'menu-your-health',
    },

    {
      id: 5,
      type: 'link',
      label: 'Meus Benefícios',
      path: '/meus-beneficios',
      icon: 'shield-heart',
      css: !user?.isAcceptedBenefitTerm ? styles.navItemWithNotification : null,
      show: clinic?.flag?.SmallBusiness,
      cy: 'menu-my-benefits',
    },
  ]

  const profileAvatarItems = [
    {
      id: 1,
      label: 'Meu Perfil',
      show: true,
      disabled: false,
      onClick: () => navigate('/perfil'),
      analyticsEvent: 'Pac_Header_MeuPerfil',
      cy: 'menu-my-profile',
    },
    {
      id: 2,
      label: 'Senha',
      show: clinic?.flag?.PermiteLoginPaciente,
      disabled: false,
      onClick: () => navigate('/confirmar-senha'),
      analyticsEvent: 'Pac_Header_Senha',
      cy: 'menu-password',
    },
    {
      id: 3,
      label: 'Meus Cartões',
      show: clinic?.canPrivateAttendance,
      disabled: false,
      onClick: () => navigate('/pagamentos'),
      analyticsEvent: 'Pac_Header_MeusCartoes',
      cy: 'menu-my-cards',
    },
    {
      id: 4,
      label: 'Trocar de conta',
      show: users?.length > 1 && !embed.navbarEmbed,
      disabled: waitingRoomMatch?.isExact,
      onClick: handleChangeProfile,
      analyticsEvent: 'Pac_Header_TrocarPerfil',
      cy: 'menu-change-profile',
    },
    {
      id: 5,
      label: 'Dúvidas',
      show: !embed.navbarEmbed,
      disabled: false,
      onClick: () => navigate('/duvidas'),
      analyticsEvent: 'Pac_Header_Duvidas',
      cy: 'menu-faq',
    },
    {
      id: 6,
      label: 'Sair',
      show: !embed.navbarEmbed,
      disabled: false,
      onClick: handleLogout,
      analyticsEvent: 'Pac_Header_Sair',
      cy: 'menu-logout',
    },
  ]

  return {
    states: {
      menuItems,
      profileAvatarItems,
      isLargeScreen,
    },
    methods: {
      handleOpenSOSDialog,
    },
  }
}
